import Media from "./utils/Media.js";
import Error from "./utils/Error.js";
import FileInput from "./utils/FileInput.js";

import Dexie from "dexie";

import AlertArea from "./ui/AlertArea.js";
import LoadingMediaArea from "./ui/LoadingMediaArea.js";

import global from "./global.js";

const log = global.log.getLogger("f_mtac.medias");
log.setLevel(log.levels.WARN);

const MEDIA_SIZE_MAX = 100000000;

const MediaNode = function (media_data) {
    const image = new Image();
    try {
        const src = URL.createObjectURL(media_data.media_data);
        var loaded = new Promise((resolve, reject) => {
            image.addEventListener(
                "load",
                () => {
                    resolve(this);
                },
                false
            );
            image.addEventListener("error", reject);
        });
        image.src = src;
        Object.defineProperties(this, {
            id: {
                set: () => {},
                get: () => media_data.local_id
            },
            width: {
                set: () => {},
                get: () => media_data.media_info.width
            },
            height: {
                set: () => {},
                get: () => media_data.media_info.height
            },
            src: {
                set: () => {},
                get: () => src
            },
            loaded: {
                set: () => {},
                get: () => loaded
            },
            image: {
                set: () => {},
                get: () => image
            },
            data: {
                set: () => {},
                get: () => media_data
            }
        });
        this.remove = function () {
            image.remove();
            URL.revokeObjectURL(src);
        };
    } catch (error) {
        log.debug("error creating object url");
        return false;
    }
};

const MediaGroup = function () {
    const nodes = {};
    this.has = function (local_id) {
        return local_id in nodes;
    };
    this.get = function (local_id) {
        return nodes[local_id];
    };
    this.update = function (modifications) {
        if (!(modifications.local_id in nodes)) {
            return Promise.resolve();
        }
        const media = nodes[modifications.local_id];
        const media_data = {};
        Object.assign(media_data, media.data);
        Object.assign(media_data, modifications);
        media.remove();
        return this.append(media_data);
    };
    this.append = function (media_data) {
        const media = new MediaNode(media_data);
        nodes[media_data.local_id] = media;
        return media.loaded;
    };
    this.remove = function (local_id) {
        if (!(local_id in nodes)) {
            return;
        }
        nodes[local_id].remove();
        delete nodes[local_id];
    };
    this.remove_all = function () {
        for (let local_id in nodes) {
            this.remove(local_id);
        }
    };
};

const Medias = function () {
    var space = null;
    var media_group = new MediaGroup();
    var file_input = new FileInput();
    this.selected = null;

    this.test_interact = () => {
        throw new Error.build.invalid({
            message: "test_interact is deprecated use handle_file instead"
        });
    };

    const handle_file_error = async (error, accept) => {
        if (LoadingMediaArea.interaction !== null) {
            LoadingMediaArea.interaction.close();
        }
        file_input.reset();
        switch (error.status) {
            case Error.status.noctx:
                throw error;
            case Error.status.invalid:
                await AlertArea.interact({
                    type: "danger",
                    tip1: global.internationalization.text("media.Media-size-too-large"),
                    tip2: global.internationalization.text("media.Select-another-media")
                });
                break;
            default:
                log.error(error);
                await AlertArea.interact({
                    type: "danger",
                    tip1: global.internationalization.text("media.Invalid-media"),
                    tip2: global.internationalization.text("media.Select-another-media")
                });
                break;
        }
        return this.interact({accept});
    };

    this.handle_file = async (file, accept) => {
        // AGPOC-9068 : global function to know ig media uploading has been cancelled
        global.server.uploadingCancelled = false;
        if (file === null) {
            return null;
        }
        try {
            if (space === null) {
                throw new Error.build.noctx({
                    message: "no space to store image"
                });
            }
            LoadingMediaArea.interact({
                title: global.internationalization.text("global.Warning"),
                tip1: global.internationalization.text("media.Media-loading-in-progress")
            });
            var media = await Media.binary_to_media(file, true);

            log.debug("media.media_data ",media.media_data);
            log.debug("media.media_info ",media.media_info);
            if (media.media_data.size > MEDIA_SIZE_MAX) {
                throw new Error.build.invalid({
                    message: "media size too big"
                });
            }
            media.media_name = file.name.split(".").slice(0, -1).join(".");
            media.media_name = media.media_name.charAt(0).toUpperCase() + media.media_name.substr(1);
            log.debug("global.server.uploadingCancelled ", global.server.uploadingCancelled);
            if (global.server.uploadingCancelled === true) {
                file_input.reset();
                return null;
            }
            media = await space.media_create(media);
            this.selected = media;
            file_input.reset();
            if (LoadingMediaArea.interaction !== null) {
                LoadingMediaArea.interaction.close();
            }
        } catch (error) {
            //log.error("medias handle file error ",error);
            // AGPOC-9068 : if operation was aborted return NULL
            if (error.message === "upload file aborted") {
                return null;
            }
            return handle_file_error(error, accept);
        }
        return media;
    };

    this.interact = async ({accept}) => {
        try {
            var file = await file_input.select(accept);
        } catch (error) {
            return handle_file_error(error, accept);
        }
        return this.handle_file(file, accept);
    };

    this.load_media = async ({local_id}) => {
        log.info("load_media", {local_id});
        if (media_group.has(local_id)) {
            return media_group.get(local_id);
        }
        let media = await space.media_retrieve({local_id});
        log.info("load_media", {media});
        if (media === undefined) {
            return;
        }
        return media_group.append(media);
    };

    this.unload_media = ({local_id}) => {
        // AGPOC-9891 : to avoid error when fallback image hasn't been loaded yet (can take some time with slow network)
        if (local_id !== undefined && media_group.has(local_id)) {
            media_group.remove(local_id);
        }
    };

    let media_on_creating = (media) => {
        var type = media.media_data.type.split("/")[0];
        if (type === "image") {
            media_group.append(media);
        }
    };

    let media_on_updating = (media, modifications) => {
        var modified = 0;
        for (let path in modifications) {
            Dexie.setByKeyPath(media, path, modifications[path]);
            modified += 1;
        }
        if (modified) {
            media_group.update(media);
        }
    };

    this.init = (new_space) => {
        if (new_space === null) {
            this.deinit();
            return;
        }
        space = new_space;
        space.on("medias", "creating", media_on_creating);
        space.on("medias", "updating", media_on_updating);
    };

    this.deinit = () => {
        if (space === null) {
            return;
        }
        space.off("medias", "creating", media_on_creating);
        space.off("medias", "updating", media_on_updating);
        media_group.remove_all();
        space = null;
    };
};

global.app.bootstrap.register_module({
    name: "medias",
    deps: ["storage"],
    init: () => {
        global.medias = new Medias();
        const space_change_handler = async ({old_space, new_space}) => {
            if (old_space === new_space) {
                return;
            }
            if (old_space !== null) {
                await global.medias.deinit();
            }
            if (new_space !== null) {
                await global.medias.init(new_space);
            } else {
                await global.medias.deinit();
            }
        };
        global.event_bus.subscribe("storage.space.changed", space_change_handler);
    }
});
